import clsx from "clsx";
import { isValidElement, useMemo, useState } from "react";
import type { ReactNode } from "react";

import i18n from "../util/i18n.ts";
import {
	chooseMobileP3ShareMode,
	createMobileP3ExportFilename,
	createMobileP3ShareText,
	getMobileP3NativeCapabilities,
	type MobileP3SharePayload,
} from "../util/mobileP3Enhancements.ts";

type MobileP3TableColumn = {
	title?: unknown;
	titleReact?: unknown;
};

type MobileP3TableRow = {
	data: readonly unknown[];
};

type MobileP3ColumnOrder = {
	colIndex: number;
	hidden?: boolean;
};

const getNodeText = (value: unknown): string => {
	if (value === null || value === undefined || typeof value === "boolean") {
		return "";
	}
	if (typeof value === "string" || typeof value === "number") {
		return String(value);
	}
	if (Array.isArray(value)) {
		return value.map(getNodeText).filter(Boolean).join(" ");
	}
	if (isValidElement(value)) {
		const props = value.props as {
			"aria-label"?: unknown;
			children?: unknown;
			title?: unknown;
		};
		return (
			getNodeText(props.children) ||
			getNodeText(props.title) ||
			getNodeText(props["aria-label"])
		);
	}
	if (typeof value === "object" && Object.hasOwn(value, "value")) {
		return getNodeText((value as { value?: unknown }).value);
	}

	return "";
};

const csvCell = (value: unknown) => {
	const text = getNodeText(value).replace(/\s+/g, " ").trim();

	return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const createMobileP3TableCsv = ({
	colOrder,
	cols,
	rows,
}: {
	colOrder?: readonly MobileP3ColumnOrder[];
	cols: readonly MobileP3TableColumn[];
	rows: readonly MobileP3TableRow[];
}) => {
	const visibleOrder =
		colOrder?.filter(({ hidden, colIndex }) => !hidden && cols[colIndex]) ??
		cols.map((_, colIndex) => ({ colIndex }));
	const header = visibleOrder.map(({ colIndex }, index) =>
		csvCell(cols[colIndex]?.titleReact ?? cols[colIndex]?.title ?? `Column ${index + 1}`),
	);
	const body = rows.map((row) =>
		visibleOrder.map(({ colIndex }) => csvCell(row.data[colIndex])).join(","),
	);

	return [header.join(","), ...body].join("\n");
};

const downloadTextFile = ({
	filename,
	mimeType = "text/plain;charset=utf-8",
	text,
}: {
	filename: string;
	mimeType?: string;
	text: string;
}) => {
	if (
		typeof document === "undefined" ||
		typeof Blob === "undefined" ||
		typeof URL === "undefined" ||
		typeof URL.createObjectURL !== "function"
	) {
		return false;
	}

	const url = URL.createObjectURL(new Blob([text], { type: mimeType }));
	const link = document.createElement("a");
	link.download = filename;
	link.href = url;
	link.style.display = "none";
	document.body?.append(link);
	link.click();
	link.remove();
	URL.revokeObjectURL(url);

	return true;
};

const resolveSharePayload = (payload: MobileP3SharePayload): MobileP3SharePayload => {
	if (!payload.url || typeof window === "undefined") {
		return payload;
	}

	try {
		return {
			...payload,
			url: new URL(payload.url, window.location.href).href,
		};
	} catch {
		return payload;
	}
};

const MobileP3ActionStatus = ({ text }: { text?: string }) =>
	text ? (
		<div className="small text-body-secondary mt-2">
			{text}
		</div>
	) : null;

const MobileP3FallbackText = ({ text }: { text?: string }) =>
	text ? (
		<textarea
			className="form-control form-control-sm mt-2"
			readOnly
			rows={3}
			value={text}
		/>
	) : null;

export const MobileP3ShareCard = ({
	children,
	className,
	kind,
	payload,
}: {
	children?: ReactNode;
	className?: string;
	kind: "draftResult" | "gameRecap" | "seasonSummary";
	payload: MobileP3SharePayload;
}) => {
	const [fallbackText, setFallbackText] = useState<string>();
	const [statusText, setStatusText] = useState<string>();

	const handleShare = async () => {
		const capabilities = getMobileP3NativeCapabilities();
		const mode = chooseMobileP3ShareMode(capabilities);
		const resolvedPayload = resolveSharePayload(payload);
		const shareText = createMobileP3ShareText(resolvedPayload);

		try {
			if (mode === "nativeShare") {
				await navigator.share?.(resolvedPayload);
			} else if (mode === "clipboard") {
				await navigator.clipboard?.writeText?.(shareText);
			} else if (mode === "download") {
				if (!downloadTextFile({
					filename: createMobileP3ExportFilename({
						extension: "txt",
						label: payload.title,
					}),
					text: shareText,
				})) {
					setFallbackText(shareText);
				}
			} else {
				setFallbackText(shareText);
			}

			setStatusText(
				i18n.t("mobileP3ShareExport.shareReady", {
					ns: "ui",
					defaultValue: "Share payload ready",
				}) || "Share payload ready",
			);
		} catch {
			setFallbackText(shareText);
			setStatusText(
				i18n.t("mobileP3ShareExport.shareFallback", {
					ns: "ui",
					defaultValue: "Share text is ready below",
				}) || "Share text is ready below",
			);
		}
	};

	return (
		<section
			className={clsx("mobile-p3-share-card", className)}
			data-mobile-p3-share-kind={kind}
		>
			<div className="mobile-p3-share-card-body">
				<div className="fw-semibold">{payload.title}</div>
				<p className="small text-body-secondary mb-2">{payload.text}</p>
				{children}
				<button
					className="btn btn-outline-secondary btn-sm"
					onClick={() => {
						void handleShare();
					}}
					type="button"
				>
					{i18n.t("mobileP3ShareExport.share", {
						ns: "ui",
						defaultValue: "Share",
					}) || "Share"}
				</button>
				<MobileP3ActionStatus text={statusText} />
				<MobileP3FallbackText text={fallbackText} />
			</div>
		</section>
	);
};

export const MobileP3TableExportButton = ({
	colOrder,
	cols,
	filenameLabel,
	rows,
	season,
}: {
	colOrder?: readonly MobileP3ColumnOrder[];
	cols: readonly MobileP3TableColumn[];
	filenameLabel: string;
	rows: readonly MobileP3TableRow[];
	season?: number;
}) => {
	const [fallbackText, setFallbackText] = useState<string>();
	const [statusText, setStatusText] = useState<string>();
	const csv = useMemo(
		() =>
			createMobileP3TableCsv({
				colOrder,
				cols,
				rows,
			}),
		[colOrder, cols, rows],
	);

	const handleExport = async () => {
		const filename = createMobileP3ExportFilename({
			label: filenameLabel,
			season,
		});
		const capabilities = getMobileP3NativeCapabilities();
		const downloaded =
			capabilities.download &&
			downloadTextFile({
				filename,
				mimeType: "text/csv;charset=utf-8",
				text: csv,
			});

		try {
			if (!downloaded && capabilities.clipboard) {
				await navigator.clipboard?.writeText?.(csv);
			} else if (!downloaded) {
				setFallbackText(csv);
			}
		} catch {
			setFallbackText(csv);
		}

		setStatusText(
			i18n.t("mobileP3ShareExport.exportReady", {
				ns: "ui",
				defaultValue: "Export ready",
			}) || "Export ready",
		);
	};

	return (
		<div className="mobile-p3-table-export">
			<button
				className="btn btn-outline-secondary btn-sm"
				onClick={() => {
					void handleExport();
				}}
				type="button"
			>
				{i18n.t("mobileP3ShareExport.export", {
					ns: "ui",
					defaultValue: "Export CSV",
				}) || "Export CSV"}
			</button>
			<MobileP3ActionStatus text={statusText} />
			<MobileP3FallbackText text={fallbackText} />
		</div>
	);
};
